import { type EditUploadContentType } from '../types';

export type VideoEditUploadContentType = Extract<EditUploadContentType, `video/${string}`>;

export type ImageEditUploadContentType = Exclude<EditUploadContentType, VideoEditUploadContentType>;

const IMAGE_MIME_TYPE_TO_EXTENSION = new Map<Exclude<EditUploadContentType, VideoEditUploadContentType>, string[]>([
  ['image/apng', ['apng']],
  ['image/avif', ['avif']],
  ['image/bmp', ['bmp']],
  ['image/gif', ['gif']],
  ['image/heic', ['heic']],
  ['image/heif', ['heif']],
  ['image/jpeg', ['jpeg', 'jpg']],
  ['image/png', ['png']],
  ['image/svg+xml', ['svg']],
  ['image/tiff', ['tif', 'tiff']],
  ['image/webp', ['webp']],
]);

const VIDEO_MIME_TYPE_TO_EXTENSION = new Map<VideoEditUploadContentType, string[]>([
  ['video/mp4', ['mp4']],
  ['video/quicktime', ['mov']],
]);

const MIME_TYPE_TO_EXTENSION = new Map<EditUploadContentType, string[]>([
  ...IMAGE_MIME_TYPE_TO_EXTENSION,
  ...VIDEO_MIME_TYPE_TO_EXTENSION,
]);

/** All content types accepted by the canvas edit upload endpoint (images + videos). */
export const ACCEPTED_UPLOAD_MIME_TYPES: readonly EditUploadContentType[] = Array.from(MIME_TYPE_TO_EXTENSION.keys());

export const isAcceptedMimeType = (mimeType: string): mimeType is EditUploadContentType =>
  ACCEPTED_UPLOAD_MIME_TYPES.some((type) => type === mimeType);

export const isVideoEditUploadContentType = (mimeType: string): mimeType is VideoEditUploadContentType =>
  isAcceptedMimeType(mimeType) && mimeType.startsWith('video/');

export const isImageEditUploadContentType = (mimeType: string): mimeType is ImageEditUploadContentType =>
  isAcceptedMimeType(mimeType) && mimeType.startsWith('image/');

export type AcceptedDroppedUploadFile = {
  file: File;
  contentType: EditUploadContentType;
};

export type DroppedUploadPartition = {
  accepted: AcceptedDroppedUploadFile[];
  unsupportedCount: number;
};

/**
 * Classifies OS file drops into accepted upload MIME types vs unsupported.
 * Videos are accepted here; size/duration limits are enforced by the canvas drop hook.
 */
export const partitionDroppedUploadFiles = (files: File[]): DroppedUploadPartition => {
  const accepted: AcceptedDroppedUploadFile[] = [];
  let unsupportedCount = 0;

  for (const file of files) {
    if (!isAcceptedMimeType(file.type)) {
      unsupportedCount++;
      continue;
    }
    accepted.push({ contentType: file.type, file });
  }

  return { accepted, unsupportedCount };
};

/**
 * File extensions the canvas can render as image nodes.
 * Derived from image MIME map only — video extensions are not included.
 */
export const CANVAS_SUPPORTED_IMAGE_EXTENSIONS: ReadonlySet<string> = new Set(
  Array.from(IMAGE_MIME_TYPE_TO_EXTENSION.values()).flat(),
);
